import { useEffect } from "react";
import { useAppSelector } from "@/store/hooks";
import { selectBranding } from "@/features/menu/menuSlice";
import type { V2Branding } from "@/types/preorderV2";
import {
  V2_ACCENT_VAR,
  V2_DEFAULT_ACCENT,
  V2_DEFAULT_ON_ACCENT,
  V2_ON_ACCENT_VAR,
} from "@/v2/tokens";

export interface BrandAccent {
  readonly accent: string;
  readonly onAccent: string;
}

/**
 * The accent pair for a restaurant's branding. The bootstrap only stores
 * hex values (parseBranding); an absent or blank value falls back to the
 * Paper default red / white.
 */
export function resolveBrandAccent(
  branding: V2Branding | null | undefined,
): BrandAccent {
  return {
    accent: branding?.primaryHex || V2_DEFAULT_ACCENT,
    onAccent: branding?.onPrimaryHex || V2_DEFAULT_ON_ACCENT,
  };
}

/**
 * Publishes the restaurant accent as `--v2-accent` / `--v2-on-accent` on the
 * document root. The root, not a wrapper element, so MUI portals (the
 * modifier sheet) see it too.
 */
export function useBrandAccent(): BrandAccent {
  const branding = useAppSelector(selectBranding);
  const { accent, onAccent } = resolveBrandAccent(branding);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(V2_ACCENT_VAR, accent);
    root.style.setProperty(V2_ON_ACCENT_VAR, onAccent);
  }, [accent, onAccent]);
  return { accent, onAccent };
}
